import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Bell, Check, ChevronRight } from 'lucide-react'
import { api } from '../lib/api'

// ─────────────────── Types ───────────────────
type Notification = {
  id: number
  event_code: string
  subject: string
  body_text: string
  link_to: string
  is_read: boolean
  read_at: string | null
  created_at: string
}

type UnreadResponse = { count: number }

// ─────────────────── Helpers ───────────────────

function relativeTime(iso: string): string {
  const dt = new Date(iso).getTime()
  const now = Date.now()
  const diffSec = Math.floor((now - dt) / 1000)
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`
  return new Date(iso).toLocaleDateString()
}

// ─────────────────── Component ───────────────────

export default function NotificationsBell() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  // Unread count — polled every 30s. Cheap (single COUNT query in backend).
  const unreadQ = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => (await api.get<UnreadResponse>('/notifications/unread-count/')).data,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })

  // Last 10 notifications — only fetched when the dropdown is opened, so
  // we don't pay the cost on every page load.
  const recentQ = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: async () => (await api.get<Notification[]>('/notifications/?limit=10')).data,
    enabled: open,
  })

  const markReadMut = useMutation({
    mutationFn: async (id: number) =>
      (await api.post(`/notifications/${id}/mark-read/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'recent'] })
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    },
  })

  const markAllReadMut = useMutation({
    mutationFn: async () => (await api.post('/notifications/mark-all-read/')).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'recent'] })
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    },
  })

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
        && buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open])

  function onRowClick(n: Notification) {
    if (!n.is_read) markReadMut.mutate(n.id)
    setOpen(false)
    if (n.link_to) navigate(n.link_to)
  }

  const unread = unreadQ.data?.count || 0
  const items = recentQ.data || []

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
        title="Notifications"
        aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-violet-600 text-white text-[10px] font-semibold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className="absolute right-0 mt-1 w-96 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-lg shadow-lg z-50"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <div className="text-sm font-semibold">
              Notifications {unread > 0 && (
                <span className="text-xs text-muted-foreground font-normal">· {unread} unread</span>
              )}
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAllReadMut.mutate()}
                disabled={markAllReadMut.isPending}
                className="text-xs text-violet-600 hover:underline inline-flex items-center gap-0.5"
              >
                <Check className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {recentQ.isLoading ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">Loading…</div>
            ) : items.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                You're all caught up.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => onRowClick(n)}
                      className={`w-full text-left px-3 py-2.5 hover:bg-muted/40 ${
                        !n.is_read ? 'bg-violet-50/50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.is_read && (
                          <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-violet-600 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{n.subject}</div>
                          {n.body_text && (
                            <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                              {n.body_text}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {relativeTime(n.created_at)}
                          </div>
                        </div>
                        {n.link_to && (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground mt-1 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="px-3 py-2 border-t border-border">
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/notifications') }}
              className="w-full text-xs text-center text-violet-600 hover:underline py-1"
            >
              See all notifications →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
